import { EmbroideryLine } from '../../../containers/EmbroideryContainer/utils/summarizeEmbroideryGroup'
import { isEmbroideryItem } from '../../../lib/utils'
import { CartItem } from '../../../types/figs'

export type MappedBundle = {
  bundleProgramHandle: string
  discountType: string
  discountValue: number
  minQty: number
  lines: Array<{
    lineId: string
    quantity: number
  }>
  totalQty: number
  qualifiesForDiscount: boolean
}

export type EmbroideryGroup = {
  guid: string
  lines: ReadonlyArray<CartItem>
}

export type CartEntry = {
  item: CartItem
  embroidery?: EmbroideryGroup
}

export type BundleGroupObject = Omit<MappedBundle, 'lines'> & {
  lines: ReadonlyArray<CartEntry>
}

type CartIndex = {
  kitGroups: CartEntry[][]
  bundleGroups: BundleGroupObject[]
  stackedItems: ReadonlyArray<CartEntry>
}

export function filterPropertiesToDisplay(
  properties: CartItem['properties']
): CartItem['properties'] {
  const propertiesToDisplay: CartItem['properties'] = {}
  Object.keys(properties).forEach(key => {
    if (key[0] !== '_') {
      propertiesToDisplay[key] = properties[key]
    }
  })
  return propertiesToDisplay
}

export function isLogoSetupInCart(cart: ReadonlyArray<CartItem>) {
  return Boolean(cart.find(item => item.properties._is_logo_setup === 'true'))
}

// Only attaches an embroidery group when the garment actually has embroidery lines
function toCartEntry(
  item: CartItem,
  ...embroiderySources: ReadonlyArray<ReadonlyMap<string, CartItem[]>>
): CartEntry {
  const guid = item.properties._guid
  if (!guid) return { item }
  const lines = embroiderySources.flatMap(source => source.get(guid) ?? [])
  return lines.length > 0 ? { item, embroidery: { guid, lines } } : { item }
}

export function buildCartIndex(
  cart: Record<string, CartItem>,
  bundlePrograms: Record<string, MappedBundle>
): CartIndex {
  const bundleLineIdLookup = new Set(
    Object.values(bundlePrograms).flatMap(bundle => bundle.lines.map(line => line.lineId))
  )

  const bundleItemMap: Map<string, CartItem> = new Map()
  // v2 garments have one embroidery line per attachment, all sharing the garment's _guid
  const embroideryByGuid: Map<string, CartItem[]> = new Map()
  const kitItemsByKitId: Map<string, CartItem[]> = new Map()
  const nonEmbroideryItems: CartItem[] = []

  for (const item of Object.values(cart)) {
    // Bucket kit items, exclude logo digitization SKUs
    if (item.properties._kitId) {
      if (!item.properties._is_logo_setup) {
        const key = item.properties._kitId
        if (!kitItemsByKitId.has(key)) {
          kitItemsByKitId.set(key, [])
        }
        kitItemsByKitId.get(key)!.push(item)
      }
      continue
    }

    // create bundleItemMap
    if (bundleLineIdLookup.has(item.cartStoreKey ?? '')) {
      bundleItemMap.set(item.cartStoreKey!, item)
      continue
    }

    // Logo digitization lines are rendered separately, never as a garment or embroidery line
    if (item.properties._is_logo_setup) continue

    if (isEmbroideryItem(item)) {
      const guid = item.properties._guid!
      if (!embroideryByGuid.has(guid)) {
        embroideryByGuid.set(guid, [])
      }
      embroideryByGuid.get(guid)!.push(item)
      continue
    }

    nonEmbroideryItems.push(item)
  }

  // Stack all groups, meaning each embroiderable item
  // is 'stacked' on top of its corresponding
  // embroidery line items in the cart
  const stackedItems: CartEntry[] = nonEmbroideryItems.map(item =>
    toCartEntry(item, embroideryByGuid)
  )

  const kitGroups: CartEntry[][] = []
  for (const kitGroup of kitItemsByKitId.values()) {
    // Kit embroidery lines carry _kitId, so they're bucketed here rather than in embroideryByGuid
    const kitEmbroideryByGuid: Map<string, CartItem[]> = new Map()
    const kitGarments: CartItem[] = []
    for (const kitItem of kitGroup) {
      if (isEmbroideryItem(kitItem)) {
        const guid = kitItem.properties._guid!
        kitEmbroideryByGuid.set(guid, [...(kitEmbroideryByGuid.get(guid) ?? []), kitItem])
      } else {
        kitGarments.push(kitItem)
      }
    }
    kitGroups.push(
      kitGarments.map(kitItem => toCartEntry(kitItem, kitEmbroideryByGuid, embroideryByGuid))
    )
  }

  // Map each bundle line to its cart entry
  // e.g. { bundle1: { lines: [{ item: cartItem1, embroidery }, { item: cartItem2 }] } }
  const bundleGroups: BundleGroupObject[] = Object.values(bundlePrograms)
    .map(bundle => ({
      ...bundle,
      lines: bundle.lines
        .map(line => {
          const cartItem = bundleItemMap.get(line.lineId)
          return cartItem ? toCartEntry(cartItem, embroideryByGuid) : undefined
        })
        .filter(entry => entry !== undefined),
    }))
    // kit items are excluded above; skip programs whose lines were all kit items
    .filter(group => group.lines.length > 0)

  return { kitGroups, bundleGroups, stackedItems }
}

type CartIndexWithPending = CartIndex & { logoSetupItem: CartItem | undefined }

// Pending lines are prepended to their section, since Shopify returns new lines first
export function buildCartIndexWithPending(
  cart: Record<string, CartItem>,
  bundlePrograms: Record<string, MappedBundle>,
  pendingLines: ReadonlyArray<CartItem>
): CartIndexWithPending {
  const live = buildCartIndex(cart, bundlePrograms)
  const logoSetupItem =
    Object.values(cart).find(item => item.properties._is_logo_setup) ??
    pendingLines.find(item => item.properties._is_logo_setup)
  if (pendingLines.length === 0) return { ...live, logoSetupItem }

  // Pending lines never have a bundle; that's assigned by the cart service
  const pending = buildCartIndex(Object.fromEntries(pendingLines.map(l => [l.key, l])), {})
  return {
    logoSetupItem,
    kitGroups: [...pending.kitGroups, ...live.kitGroups],
    bundleGroups: live.bundleGroups,
    stackedItems: [...pending.stackedItems, ...live.stackedItems],
  }
}

export function hasPlaceholderLine(entry: CartEntry): boolean {
  return Boolean(entry.item.isPlaceholder || entry.embroidery?.lines.some(l => l.isPlaceholder))
}

// Kit display order: top, pants, then the rest. Placeholders only know `productType`.
export function orderKitEntries(entries: ReadonlyArray<CartEntry>): CartEntry[] {
  const category = ({ item }: CartEntry) =>
    (item.categoryId || item.productType).toLocaleLowerCase()
  const topEntry = entries.find(e => category(e).includes('scrub top'))
  const pantsEntry = entries.find(e => category(e).includes('scrub pants'))
  const otherEntries = entries.filter(e => e !== topEntry && e !== pantsEntry)
  return [topEntry, pantsEntry, ...otherEntries].filter(e => e !== undefined)
}

// Cart prices are unit prices in cents
export function toEmbroideryLine(item: CartItem): EmbroideryLine {
  return {
    key: item.key,
    properties: item.properties,
    price: { effectivePrice: item.effectivePrice, fullPrice: item.fullPrice },
  }
}

export function getEntryKeys(entries: ReadonlyArray<CartEntry>): string[] {
  return entries.flatMap(({ item, embroidery }) => [
    item.key,
    ...(embroidery?.lines.map(l => l.key) ?? []),
  ])
}
